// Shared request-result handling for the generated api-client (doctrine 3: every
// screen consumes @assessly/api-client only). openapi-fetch returns
// `{ data, error, response }`; these helpers normalise that into a thrown
// `RequestError` carrying the RFC 9457 problem fields the UI needs — the
// `request_id` for the §4 error card and the field-level `errors[]` (message
// keys, D-022) the schema form surfaces verbatim.

import type { components } from "@assessly/api-client";
import { UnauthorizedError } from "./problem";

export type Problem = components["schemas"]["Problem"];
export type FieldError = NonNullable<Problem["errors"]>[number];

/** The `{ data, error, response }` envelope every openapi-fetch call returns. */
export interface FetchResult<T> {
  data?: T | undefined;
  error?: unknown;
  response: Response;
}

function isProblem(value: unknown): value is Problem {
  return typeof value === "object" && value !== null && "title" in value && "status" in value;
}

/**
 * A failed API call. Message is the problem `title` (falling back to a caller
 * label); `requestId` and `fieldErrors` power the error card and the schema
 * form. Screens branch on `instanceof UnauthorizedError` first for the 401
 * redirect, then render this.
 */
export class RequestError extends Error {
  readonly requestId: string | undefined;
  readonly code: string | undefined;
  readonly httpStatus: number;
  readonly fieldErrors: readonly FieldError[];
  readonly problem: Problem | undefined;
  /**
   * The server's own `Retry-After`, in ms, when it named one. `RESOURCE_BUSY`
   * (503) and `IDEMPOTENCY_IN_PROGRESS` (409) both ship it, and both mean "come
   * back very soon" — a client that ignores it and applies its own exponential
   * backoff turns a one-second wait into minutes. Only the floor outbox acts on
   * it today; it is carried here so no caller has to reach for the raw Response.
   */
  readonly retryAfterMs: number | undefined;

  constructor(
    problem: Problem | undefined,
    httpStatus: number,
    fallbackTitle: string,
    retryAfterMs?: number,
  ) {
    super(problem?.title ?? fallbackTitle);
    this.name = "RequestError";
    this.requestId = problem?.request_id;
    this.code = problem?.code;
    this.httpStatus = httpStatus;
    this.fieldErrors = problem?.errors ?? [];
    this.problem = problem;
    this.retryAfterMs = retryAfterMs;
  }
}

/** `Retry-After` as ms. Delta-seconds only — the HTTP-date form is not emitted. */
function retryAfterMsOf(response: Response): number | undefined {
  let raw: string | null = null;
  try {
    raw = response.headers.get("retry-after");
  } catch {
    return undefined;
  }
  if (raw === null) return undefined;
  const seconds = Number(raw.trim());
  if (!Number.isFinite(seconds) || seconds < 0) return undefined;
  return Math.min(seconds, 86_400) * 1000;
}

/**
 * Throw on a non-2xx result, otherwise return the decoded body. A 401 becomes
 * `UnauthorizedError` so callers can redirect to /login (matching the P0
 * products screen).
 */
export function unwrap<T>(result: FetchResult<T>, fallbackTitle: string): T {
  if (result.response.status === 401) throw new UnauthorizedError();
  if (result.error !== undefined || result.data === undefined) {
    const problem = isProblem(result.error) ? result.error : undefined;
    throw new RequestError(
      problem,
      result.response.status,
      fallbackTitle,
      retryAfterMsOf(result.response),
    );
  }
  return result.data;
}

/** Like {@link unwrap} but for 204/no-content mutations (DELETE, etc.). */
export function unwrapVoid(result: FetchResult<unknown>, fallbackTitle: string): void {
  if (result.response.status === 401) throw new UnauthorizedError();
  if (result.error !== undefined || !result.response.ok) {
    const problem = isProblem(result.error) ? result.error : undefined;
    throw new RequestError(
      problem,
      result.response.status,
      fallbackTitle,
      retryAfterMsOf(result.response),
    );
  }
}

/** A fresh Idempotency-Key for a mutation (spec 16 §5; §9.15). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** Read the request id off any thrown error, for the §4 error card. */
export function requestIdOf(error: unknown): string | undefined {
  return error instanceof RequestError ? error.requestId : undefined;
}
